/**
 * CreateMailshotModal Component
 *
 * Modal for creating new mailshots within a campaign
 */

import { useState, useEffect } from "react";
import { FaEnvelope, FaTimes, FaSpinner } from "react-icons/fa";
import {
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useToast } from "./Toast";
import "./CreateMailshotModal.scss";

export const CreateMailshotModal = ({
  isOpen,
  onClose,
  campaignId,
  campaignName,
  onSuccess,
}) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: "",
    selectedCampaignId: campaignId || "",
  });
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  // Fetch campaigns if no campaignId provided
  useEffect(() => {
    if (isOpen && !campaignId) {
      fetchCampaigns();
    }
  }, [isOpen, campaignId]);

  // Update selectedCampaignId when campaignId prop changes
  useEffect(() => {
    if (campaignId) {
      setFormData((prev) => ({
        ...prev,
        selectedCampaignId: campaignId,
      }));
    }
  }, [campaignId]);

  const fetchCampaigns = async () => {
    try {
      setLoadingCampaigns(true);
      const campaignsQuery = query(
        collection(db, "discovery_campaigns"),
        orderBy("createdAt", "desc")
      );
      const campaignsSnapshot = await getDocs(campaignsQuery);
      const campaignsData = campaignsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCampaigns(campaignsData);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast.error("Failed to load campaigns");
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.warning("Please enter a mailshot name");
      return;
    }

    const targetCampaignId = campaignId || formData.selectedCampaignId;
    if (!targetCampaignId) {
      toast.error("Please select a campaign");
      return;
    }

    setLoading(true);
    try {
      const newMailshot = {
        name: formData.name.trim(),
        campaignId: targetCampaignId,
        propertyIds: [],
        createdAt: serverTimestamp(),
        mailedOut: false,
        mailedOutAt: null,
        stats: {
          totalProperties: 0,
          duplicateCount: 0,
          sentCount: 0,
        },
      };

      const docRef = await addDoc(
        collection(db, "discovery_mailshots"),
        newMailshot
      );

      toast.success(`Mailshot "${formData.name}" created successfully`);

      // Reset form
      setFormData({ name: "", selectedCampaignId: campaignId || "" });

      // Call success callback
      if (onSuccess) {
        onSuccess(docRef.id);
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error("Error creating mailshot:", error);
      if (error.code === "permission-denied") {
        toast.error("Permission denied: Only admins can create mailshots.");
      } else {
        toast.error("Failed to create mailshot: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ name: "", selectedCampaignId: campaignId || "" });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-mailshot-modal-overlay" onClick={handleClose}>
      <div
        className="create-mailshot-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="create-mailshot-modal__header">
            <div className="create-mailshot-modal__title">
              <FaEnvelope />
              Create New Mailshot
            </div>
            <button
              type="button"
              className="create-mailshot-modal__close"
              onClick={handleClose}
              disabled={loading}
            >
              <FaTimes />
            </button>
          </div>

          <div className="create-mailshot-modal__body">
            {!campaignId && (
              <div className="form-group">
                <label htmlFor="campaign">
                  Campaign <span className="required">*</span>
                </label>
                <select
                  id="campaign"
                  name="selectedCampaignId"
                  value={formData.selectedCampaignId}
                  onChange={handleChange}
                  required
                  disabled={loading || loadingCampaigns}
                >
                  <option value="">Select a campaign...</option>
                  {campaigns.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </option>
                  ))}
                </select>
                {campaigns.length === 0 && !loadingCampaigns && (
                  <span className="help-text" style={{ color: "#ef4444" }}>
                    No campaigns available. Please create a campaign first.
                  </span>
                )}
              </div>
            )}

            {campaignId && (
              <p className="subtitle">
                Creating a mailshot in <strong>{campaignName}</strong>
              </p>
            )}

            <div className="form-group">
              <label htmlFor="name">
                Mailshot Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Wave 1 - New Listings"
                required
                disabled={loading}
                autoFocus
              />
              <span className="help-text">
                Give this mailshot a descriptive name to identify it within the
                campaign
              </span>
            </div>
          </div>

          <div className="create-mailshot-modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={loading || !formData.name.trim()}
            >
              {loading ? (
                <>
                  <FaSpinner className="spinner" />
                  Creating...
                </>
              ) : (
                "Create Mailshot"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
